/** Shared preload promises — parallel fetch, no duplicate chunk requests. */

import {
  ensureAuthDialogReady,
  loadBootstrapStyles,
  loadFontAwesomeStyles,
  loadPrimeReactStyles,
  preloadAuthDialogChunk,
} from '../../../shared-lib/utils/deferred-app-styles';
import { preloadFullAppUpgrade, prepareFullAppUpgrade } from '../../../bootstrap/upgrade-to-full-app';

const LANDING_HEADER_LOGO = require('../../../shared-lib/assets/img/virtup-logo-black.webp');

let landingPagePromise: Promise<void> | null = null;

export function preloadHomeLandingPage(): Promise<void> {
  if (!landingPagePromise) {
    landingPagePromise = import('./home-landing-page').then(() => undefined);
  }
  return landingPagePromise;
}

let belowFoldPromise: Promise<void> | null = null;

export function preloadHomeLandingBelowFold(): Promise<void> {
  if (!belowFoldPromise) {
    belowFoldPromise = import('./home-landing-below-fold').then(() => undefined);
  }
  return belowFoldPromise;
}

let shellHeaderPromise: Promise<void> | null = null;

export function preloadHomeShellHeader(): Promise<void> {
  if (!shellHeaderPromise) {
    shellHeaderPromise = import(
      '../../../shared-lib/layout/topheader-component/top-headernew'
    ).then(() => undefined);
  }
  return shellHeaderPromise;
}

let shellSignalRPromise: Promise<typeof import('../../../chat/context/SignalRContext')> | null =
  null;

export function preloadHomeShellSignalR(): Promise<
  typeof import('../../../chat/context/SignalRContext')
> {
  if (!shellSignalRPromise) {
    shellSignalRPromise = import('../../../chat/context/SignalRContext');
  }
  return shellSignalRPromise;
}

let headerStylesPromise: Promise<void> | null = null;

/** Bootstrap grid/utilities needed before header paints (avoids white unstyled flash). */
export function ensureHomeShellHeaderStyles(): Promise<void> {
  if (!headerStylesPromise) {
    headerStylesPromise = Promise.all([
      Promise.resolve().then(() => {
        loadBootstrapStyles();
        loadPrimeReactStyles();
      }),
      loadFontAwesomeStyles(),
      preloadAuthDialogChunk(),
    ]).then(() => undefined);
  }
  return headerStylesPromise;
}

let headerLogoPromise: Promise<void> | null = null;

/** Landing dark header uses the black logo asset. */
export function preloadHeaderLogo(): Promise<void> {
  if (typeof window === 'undefined') {
    return Promise.resolve();
  }

  if (!headerLogoPromise) {
    headerLogoPromise = new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve();
      img.onerror = () => resolve();
      img.src = LANDING_HEADER_LOGO;
      if (img.complete) {
        resolve();
      }
    });
  }

  return headerLogoPromise;
}

/** After shell reveal, warm auth + full app so off-shell nav is smooth. */
export function preloadAuthDialogAfterShellReveal(): void {
  void ensureAuthDialogReady();
  void prepareFullAppUpgrade();
}

const HERO_BG_VIDEO_WEBM = '/media/home-hero-bg-animation-new.webm';
const HERO_BG_VIDEO_MP4 = '/media/home-hero-bg-animation-new.mp4';

let heroVideoPromise: Promise<void> | null = null;

/** Warm browser cache for the hero background video while the shell loader is visible. */
export function preloadHeroBackgroundVideo(): Promise<void> {
  if (typeof window === 'undefined') {
    return Promise.resolve();
  }

  if (!window.matchMedia('(min-width: 768px)').matches) {
    return Promise.resolve();
  }

  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    return Promise.resolve();
  }

  if (!heroVideoPromise) {
    heroVideoPromise = new Promise((resolve) => {
      const video = document.createElement('video');
      video.preload = 'auto';
      video.muted = true;
      video.playsInline = true;

      const finish = () => {
        video.removeEventListener('canplaythrough', finish);
        video.removeEventListener('loadeddata', finish);
        video.removeEventListener('error', finish);
        window.clearTimeout(timeoutId);
        resolve();
      };

      video.addEventListener('canplaythrough', finish, { once: true });
      video.addEventListener('loadeddata', finish, { once: true });
      video.addEventListener('error', finish, { once: true });

      const timeoutId = window.setTimeout(finish, 15000);
      const prefersWebm = video.canPlayType('video/webm') !== '';
      video.src = prefersWebm ? HERO_BG_VIDEO_WEBM : HERO_BG_VIDEO_MP4;
      video.load();
    });
  }

  return heroVideoPromise;
}
